import { faRegularClockCircleExclamation } from '@awesome.me/kit-2cbcf7d4a0/icons/kit/custom';
import { orderBy } from '@progress/kendo-data-query';
import {
    CompositeFilterDescriptor,
    FilterDescriptor,
} from '@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface';
import { GridCellProps, GridCustomCellProps } from '@progress/kendo-react-grid';
import { format } from 'date-fns';
import { IGridHelper } from './helpers/interfaces';
import useGridCustomCell from '@components-clean/core/grid/custom-cell';
import {
    FilterOperators,
    GridFilter,
    GridSearch,
    RAGFilterChipValues,
    RAGFilterType,
} from '@components-clean/core/grid/grid-custom-filter/grid-custom-filter.interface';
import { GridColumnProps } from '@components-clean/core/grid/grid.interface';
import { MultiValueCell, MultiValueDataItem } from '@components-clean/core/grid/multi-value-cell';
import { DateCell, MultiValueDataCell, PartialDateCell } from '@components-clean/core/grid-fe/custom-cells';
import { LogicOperator } from '@components-clean/core/study-data-filter/advanced-select/advanced-select.interface';
import {
    AdvanceFilter,
    ColumnDefinition,
    MultiValueType,
    MultiValueTypeEnum,
    RAGType,
    RAGTypeEnum,
} from '@interfaces/central-monitoring';
import { NonRAGDataItem } from '@pages-clean/dummy-page/dummy-page.data';

export const API_GRID_TAKE = 50;
export const DEFAULT_GRID_TABLE = 'datatable';

export enum SortDir {
    asc = 0,
    desc = 1,
}

export function instanceOfGridSearch(object: unknown): object is GridSearch {
    return object !== null && typeof object === 'object' && 'search' in object;
}

export function instanceOfGridSearchExtendedClient(object: unknown): object is GridSearch {
    return (
        (object !== null && typeof object === 'object' && ('search' in object || 'value1' in object)) ||
        typeof object === 'boolean' ||
        typeof object === 'string'
    );
}

export function instanceOfGridSearchExtended(object: unknown): object is GridSearch {
    return (
        (object !== null &&
            typeof object === 'object' &&
            (('search' in object && 'type' in object) || 'value1' in object)) ||
        typeof object === 'boolean' ||
        typeof object === 'string'
    );
}

export function instanceOfBasicGridSearch(object: unknown): object is GridSearch {
    return (
        typeof object === 'boolean' ||
        typeof object === 'string' ||
        typeof object === 'number' ||
        (object !== null && typeof object === 'object' && 'search' in object)
    );
}

export function instanceOfGridFilter(object: unknown): object is GridFilter {
    return (
        object !== null &&
        typeof object === 'object' &&
        'filters' in object &&
        Array.isArray((object as GridFilter).filters)
    );
}

export function instanceOfCompositeFilterDescriptor(
    object: FilterDescriptor | CompositeFilterDescriptor,
): object is CompositeFilterDescriptor {
    return 'logic' in object;
}

export const scrollGridToTop = (testId: string) => {
    const grid = document.querySelector(`[data-testid='${testId}']`);
    const gridContainer = grid?.querySelector('.k-grid-content');

    if (gridContainer) {
        gridContainer.scrollTop = 0;
    }
};

const getColumnTypeCell = (columnType: ColumnDefinition, helper: IGridHelper) => {
    const { IconAndToolTipCell } = useGridCustomCell();
    switch (columnType.columnType) {
        case 1:
            if (!columnType.multiValueCell) return undefined;
            return (props: GridCellProps) => {
                const item = props.dataItem;
                const isHistoricalField = columnType.dataField?.startsWith('previous_');
                const parentField = columnType.dataField?.replace('previous_', '') as string;
                const changeField = `${parentField}_change_direction`;
                const scoreRag = `${parentField}_rag`;
                const previousRag = `previous_${parentField}_rag`;
                const dataItem: MultiValueDataItem = {
                    valueAfter: item[parentField],
                    valueBefore: isHistoricalField ? item[columnType.dataField as string] : item.null,
                    changeDirection: +item[changeField],
                    colourBefore: item[previousRag] as string,
                    colourAfter: (item[scoreRag] as string) ?? undefined,
                };

                return MultiValueCell({
                    ...props,
                    field: 'Score',
                    dataItem,
                    highlighted: helper.isRagColumn(columnType),
                    showBlankText: false,
                });
            };
        case 3:
            if (!columnType.multiValueCell) return undefined;
            return (props: GridCustomCellProps) => {
                const item = props.dataItem;
                const x: MultiValueDataItem = {
                    colourAfter: '',
                    colourBefore: '',
                    valueAfter: 0,
                    valueBefore: 0,
                };
                const dataItem: NonRAGDataItem = {
                    ...x,
                    Score: {
                        valueInFocus: item[`${columnType.dataField as string}`],
                        comparisonValue: item[`previous_${columnType.dataField as string}`],
                        changeDirection: +item[`${columnType.dataField as string}_change_direction`],
                    },
                    Change: 1,
                };

                // @ts-ignore
                return MultiValueDataCell({
                    ...props,
                    dataItem,
                    field: 'Score',
                    highlighted: false,
                });
            };
        case 2:
            return (props: GridCustomCellProps) => {
                const displayIcon = props.dataItem.last_reviewed_display_icon;

                const title = "Subject's last review was performed after the selected dataset";
                return DateCell({
                    icon: displayIcon === 'Yes' ? faRegularClockCircleExclamation : undefined,
                    title,
                    ...props,
                });
            };
        case 4:
            return PartialDateCell;
        case 7:
            return (props: GridCellProps) => {
                const { dataItem } = props;
                let tagsArray: string[] = [];

                if (dataItem.tags && dataItem.tags !== '') {
                    tagsArray = dataItem.tags
                        .split(',')
                        .map((tag: string) => tag.trim())
                        .filter((tag: string) => tag.length > 0);
                }

                return IconAndToolTipCell({
                    ...props,
                    dataItem: {
                        tooltipItems: tagsArray,
                        icon: 'tag',
                        tooltipItemsCount: dataItem.tag_count,
                        title: 'Tags',
                        noItemsMessage: 'No Tags',
                    },
                });
            };
        default:
            return undefined;
    }
};

const getFilterTypeCell = (column: ColumnDefinition) => {
    switch (column.columnType) {
        // Dates need to be formatted
        case 2:
        case 4:
            return column.lovData?.map((item) => format(new Date(item), 'dd-MMM-yyyy'));
        default:
            return column.lovData ?? undefined;
    }
};

const getColumnType = (columnType: ColumnDefinition): GridColumnProps['filter'] => {
    switch (columnType.columnType) {
        case 0:
            return 'text';
        case 1:
            // @ts-ignore
            return 'float';
        case 3:
            return 'numeric';
        case 2:
        case 4:
            return 'date';
        case 5:
            return 'boolean';
        default:
            return undefined;
    }
};

const minColTypeWidths: Map<number | undefined, number> = new Map([
    [0, 225], // Text Column
    [1, 100], // Float Column
    [2, 170], // Date Column
    [3, 100], // Numeric Column
    [4, 140], // Partial Date Column
    [5, 70], // Boolean Column,
    [7, 100], // Icon Column
]);

export const formGridColumns = (
    columns: ColumnDefinition[],
    helper: IGridHelper,
    isDataTableGrid: boolean,
): GridColumnProps[] => {
    const { TooltipCell } = useGridCustomCell();
    return orderBy(columns, [{ field: 'columnPosition', dir: 'asc' }]).map((column) => {
        const width = isDataTableGrid
            ? minColTypeWidths.get(column.columnType)
            : (helper.getColumnWidth(column) ?? column.defaultWidth);
        // Column Type 0 is text column, We need to display tooltips on all text type columns in data tables.
        const showTooltip = isDataTableGrid && column.columnType === 0 ? true : helper.getDisplayTooltipFields(column);
        const activeField = helper.getActiveField(column);
        const customCell = helper.getCustomCell(column);
        const lovMultiSelect = helper.lovMultiSelect != null ? helper.lovMultiSelect(column) : undefined;

        return {
            id: column.dataField ?? '',
            field: column.dataField ?? '',
            title: column.title ?? '',
            minWidth: width,
            locked: column.lockedByDefault,
            lockedByDefault: column.lockedByDefault,
            filter: getColumnType(column),
            lovData: getFilterTypeCell(column),
            lovMultiSelect,
            show: helper.getDisplayFields(column),
            activeField,
            children: formGridColumns(column?.children ?? [], helper, isDataTableGrid),
            columnPosition: column.columnPosition,
            ragColumn: helper.isRagColumn(column),
            showByDefault: helper.getDisplayFields(column),
            showHeaderToolTip: isDataTableGrid,
            sortable: column.children == null || column.children?.length === 0,
            cells: {
                data: !!customCell
                    ? customCell
                    : showTooltip
                      ? TooltipCell({
                            activeField: helper.getActiveField(column),
                        })
                      : getColumnTypeCell(column, helper),
            },
            disableLock: helper.disableLock(column),
            multiValueTypeColumn: helper.multiValueFilter(column),
            hideAdvancedFilter: helper.disableAdvancedFilter(column),
        } satisfies GridColumnProps;
    });
};

const convertMultiValueType = (value?: RAGFilterType): MultiValueType | undefined => {
    if (value == null) return undefined;
    switch (value) {
        case RAGFilterType.Direction:
            return MultiValueTypeEnum.Direction;
        case RAGFilterType.Current:
            return MultiValueTypeEnum.Current;
        case RAGFilterType.Previous:
            return MultiValueTypeEnum.Previous;
        default:
            window.console.error('Unsupported RAGFilterType for API Grid');
            return undefined;
    }
};

const convertRAGType = (value?: RAGFilterChipValues): RAGType | undefined => {
    if (value == null) return undefined;
    switch (value) {
        case RAGFilterChipValues.Value:
            return RAGTypeEnum.Value;
        case RAGFilterChipValues.Color:
            return RAGTypeEnum.Colour;
        default:
            window.console.error('Unsupported RAGFilterChipValues for API Grid');
            return undefined;
    }
};

export const formFilters = (filterData: CompositeFilterDescriptor | undefined, table = DEFAULT_GRID_TABLE) => {
    const filtersData: AdvanceFilter[] = [];

    if (filterData?.filters.length) {
        filterData.filters.forEach((item) => {
            let filterField = '';
            let columnFilters: GridFilter = { logic: null, filters: [] };

            if (!instanceOfCompositeFilterDescriptor(item) && item.value && instanceOfGridSearch(item.value)) {
                filtersData.push({
                    logic: LogicOperator.AND,
                    table,
                    field: item.field as string,
                    multiValueType: item.value.multiValueType ? MultiValueTypeEnum.PreviousOrCurrent : undefined,
                    ragType: item.value.multiValueType ? RAGTypeEnum.Value : undefined,
                    filters: [
                        {
                            table,
                            field: item.field as string,
                            operator: FilterOperators.Search,
                            value: item.value.search,
                            // @ts-ignore NOTE: there is no DateTime type yet
                            type: item.value.type,
                            multiValueType: item.value.multiValueType
                                ? MultiValueTypeEnum.PreviousOrCurrent
                                : undefined,
                            ragType: item.value.multiValueType ? RAGTypeEnum.Value : undefined,
                        },
                    ],
                });
            } else if (!instanceOfCompositeFilterDescriptor(item) && (item.value as GridFilter).filters) {
                filterField = item.field as string;
                columnFilters = item.value as GridFilter;

                filtersData.push({
                    logic: columnFilters?.logic ?? undefined,
                    table,
                    field: filterField,
                    multiValueType: convertMultiValueType(columnFilters.multiValueType),
                    ragType: convertRAGType(columnFilters.ragType),
                    filters: columnFilters.filters.map((filterItem) => ({
                        table,
                        field: filterField,
                        operator: filterItem.operator ?? undefined,
                        value: filterItem.value as string,
                        type: filterItem.dataType as AdvanceFilter['type'],
                        multiValueType: convertMultiValueType(filterItem.multiValueType),
                        ragType: convertRAGType(filterItem.ragType),
                    })),
                });
            }
        });
    }
    return filtersData;
};
